import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { adminApi } from '../../utils/api';
import Button from './shared/Button';
import Badge from './shared/Badge';
import Spinner from './shared/Spinner';
import Alert from './shared/Alert';
import Modal from './shared/Modal';

const SCHEDULE_LABELS = {
    daily: 'Hằng ngày',
    weekly: 'Hằng tuần',
    monthly: 'Hằng tháng',
    cron: 'Cron',
    trigger: 'Thủ công',
};

const STATUS_LABELS = {
    success: 'Thành công',
    failed: 'Thất bại',
    running: 'Đang chạy',
    timeout: 'Quá hạn',
    cancelled: 'Đã huỷ',
};

function formatSchedule(config) {
    const schedule = config?.schedule || {};
    const type = schedule.type || 'daily';
    const time = schedule.time || '07:00';
    if (type === 'cron') return schedule.cron_expression || '-';
    if (type === 'trigger') return SCHEDULE_LABELS.trigger;
    const label = SCHEDULE_LABELS[type] || type;
    if (type === 'weekly') return `${label} (thứ ${schedule.day_of_week ?? '-'}) ${time}`;
    if (type === 'monthly') return `${label} (ngày ${schedule.day_of_month ?? 1}) ${time}`;
    return `${label} ${time}`;
}

function StatusBadge({ status }) {
    if (!status) return <Badge variant="secondary">-</Badge>;
    const map = { success: 'success', failed: 'danger', running: 'warning', timeout: 'warning', cancelled: 'secondary' };
    return <Badge variant={map[status] || 'secondary'}>{STATUS_LABELS[status] || status}</Badge>;
}

export default function AdminScheduledTasks() {
    const navigate = useNavigate();
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [runsModal, setRunsModal] = useState(null);
    const [runs, setRuns] = useState([]);
    const [runsLoading, setRunsLoading] = useState(false);
    const [runDetail, setRunDetail] = useState(null);

    useEffect(() => { loadTasks(); }, []);

    useEffect(() => {
        if (successMessage) {
            const t = setTimeout(() => setSuccessMessage(null), 5000);
            return () => clearTimeout(t);
        }
    }, [successMessage]);

    const loadTasks = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await adminApi.listScheduledTasks();
            setTasks(Array.isArray(data) ? data : []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (e, taskId, taskName) => {
        e.stopPropagation();
        if (!confirm(`Xoá tác vụ "${taskName}"?`)) return;
        try {
            await adminApi.deleteScheduledTask(taskId);
            setSuccessMessage(`Đã xoá tác vụ "${taskName}"`);
            loadTasks();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleRun = async (e, taskId, taskName) => {
        e.stopPropagation();
        try {
            await adminApi.runScheduledTask(taskId);
            setSuccessMessage(`Đã kích hoạt tác vụ "${taskName}"`);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleCancel = async (e, taskId, taskName) => {
        e.stopPropagation();
        if (!confirm(`Huỷ toàn bộ lượt đang chạy của "${taskName}"?`)) return;
        try {
            const result = await adminApi.cancelScheduledTask(taskId);
            setSuccessMessage(`Đã huỷ ${result.cancelled_in_memory} lượt, đánh dấu ${result.marked_orphan_as_cancelled} lượt mồ côi`);
            loadTasks();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleCancelRun = async (taskId, runId) => {
        if (!confirm(`Huỷ lượt #${runId}?`)) return;
        try {
            await adminApi.cancelScheduledTaskRun(taskId, runId);
            setSuccessMessage(`Đã huỷ lượt #${runId}`);
            if (runsModal) {
                const data = await adminApi.listScheduledTaskRuns(runsModal.taskId);
                setRuns(Array.isArray(data) ? data : []);
            }
        } catch (err) {
            setError(err.message);
        }
    };

    const handleViewRuns = async (e, taskId, taskName) => {
        e.stopPropagation();
        setRunsModal({ taskId, taskName });
        setRunsLoading(true);
        try {
            const data = await adminApi.listScheduledTaskRuns(taskId);
            setRuns(Array.isArray(data) ? data : []);
        } catch (err) {
            setError(err.message);
        } finally {
            setRunsLoading(false);
        }
    };

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <h1 className="text-lg font-semibold text-gray-900">Tác vụ lập lịch</h1>
                <Button onClick={() => navigate('/admin/scheduled-tasks/new')}>
                    Tạo tác vụ
                </Button>
            </div>

            {error && <Alert variant="error" className="mb-4" onClose={() => setError(null)}>{error}</Alert>}
            {successMessage && <Alert variant="success" className="mb-4">{successMessage}</Alert>}

            {loading ? (
                <Spinner size="lg" text="Đang tải danh sách..." center />
            ) : tasks.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                    Chưa có tác vụ nào. Tạo tác vụ mới để bắt đầu.
                </div>
            ) : (
                <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto shadow-sm">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-gray-200 bg-gray-50/60">
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Tác vụ</th>
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Trợ lý</th>
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Lịch</th>
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Trạng thái</th>
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Kích hoạt</th>
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Số lượt</th>
                                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500 uppercase">Thao tác</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {tasks.map(task => (
                                <tr
                                    key={task.id}
                                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                                    onClick={() => navigate(`/admin/scheduled-tasks/${task.id}/edit`)}
                                >
                                    <td className="px-4 py-3">
                                        <div className="font-medium text-gray-900">{task.name}</div>
                                        <div className="text-xs text-gray-400 font-mono">{task.id}</div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <Badge variant="info">{task.agent_id || task.config?.agent_id || '-'}</Badge>
                                    </td>
                                    <td className="px-4 py-3 text-gray-600 text-xs font-mono">
                                        {formatSchedule(task.config)}
                                    </td>
                                    <td className="px-4 py-3">
                                        <StatusBadge status={task.last_status} />
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`inline-block w-2 h-2 rounded-full ${task.is_active ? 'bg-green-500' : 'bg-gray-300'}`} />
                                    </td>
                                    <td className="px-4 py-3 text-gray-500">
                                        {task.run_count ?? 0}
                                    </td>
                                    <td className="px-4 py-3 text-right whitespace-nowrap">
                                        {task.last_status === 'running' ? (
                                            <Button
                                                variant="ghost" size="xs" className="text-orange-600"
                                                onClick={(e) => handleCancel(e, task.id, task.name)}
                                            >
                                                Huỷ
                                            </Button>
                                        ) : (
                                            <Button
                                                variant="ghost" size="xs"
                                                onClick={(e) => handleRun(e, task.id, task.name)}
                                            >
                                                Chạy ngay
                                            </Button>
                                        )}
                                        <Button
                                            variant="ghost" size="xs"
                                            onClick={(e) => handleViewRuns(e, task.id, task.name)}
                                        >
                                            Lịch sử
                                        </Button>
                                        <Button
                                            variant="ghost" size="xs"
                                            onClick={(e) => { e.stopPropagation(); navigate(`/admin/scheduled-tasks/new?clone=${task.id}`); }}
                                        >
                                            Nhân bản
                                        </Button>
                                        <Button
                                            variant="ghost" size="xs"
                                            onClick={(e) => { e.stopPropagation(); navigate(`/admin/scheduled-tasks/${task.id}/edit`); }}
                                        >
                                            Sửa
                                        </Button>
                                        <Button
                                            variant="ghost" size="xs" className="text-red-600"
                                            onClick={(e) => handleDelete(e, task.id, task.name)}
                                        >
                                            Xoá
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <Modal
                open={!!runsModal}
                title={runsModal ? `Lịch sử lượt chạy — ${runsModal.taskName}` : ''}
                onClose={() => { setRunsModal(null); setRuns([]); setRunDetail(null); }}
                size="xl"
            >
                <div className="p-4">
                    {runsLoading ? (
                        <Spinner size="md" text="Đang tải..." center />
                    ) : runs.length === 0 ? (
                        <p className="text-gray-500 text-center py-6">Chưa có lượt chạy nào</p>
                    ) : (
                        <div className="max-h-[50vh] overflow-y-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b bg-gray-50">
                                        <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">#</th>
                                        <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Bắt đầu</th>
                                        <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Trạng thái</th>
                                        <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Thời lượng</th>
                                        <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Đầu ra</th>
                                        <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Thao tác</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {runs.map(run => (
                                        <tr
                                            key={run.id}
                                            className={`cursor-pointer transition-colors ${runDetail?.id === run.id ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                                            onClick={() => setRunDetail(runDetail?.id === run.id ? null : run)}
                                        >
                                            <td className="px-3 py-2 text-xs text-gray-400 font-mono">{run.id}</td>
                                            <td className="px-3 py-2 text-xs text-gray-600">
                                                {run.started_at ? new Date(run.started_at).toLocaleString() : '-'}
                                            </td>
                                            <td className="px-3 py-2"><StatusBadge status={run.status} /></td>
                                            <td className="px-3 py-2 text-xs text-gray-600">
                                                {run.duration_ms ? `${(run.duration_ms / 1000).toFixed(1)}s` : '-'}
                                            </td>
                                            <td className="px-3 py-2 text-xs">
                                                {Array.isArray(run.notification_results) && run.notification_results.length > 0 ? (
                                                    <div className="flex flex-wrap gap-1">
                                                        {run.notification_results.map((r, i) => (
                                                            <Badge key={i} variant={r.ok ? 'success' : 'danger'}>
                                                                {r.type}{r.ok ? '' : ' · lỗi'}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                ) : '-'}
                                            </td>
                                            <td className="px-3 py-2 text-right">
                                                {run.status === 'running' && (
                                                    <Button
                                                        variant="ghost" size="xs" className="text-orange-600"
                                                        onClick={(e) => { e.stopPropagation(); handleCancelRun(runsModal.taskId, run.id); }}
                                                    >
                                                        Huỷ
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </Modal>

            <Modal
                open={!!runDetail}
                title={runDetail ? `Chi tiết lượt chạy #${runDetail.id}` : ''}
                onClose={() => setRunDetail(null)}
                size="xl"
            >
                {runDetail && (
                    <div className="p-5 space-y-4">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <span className="text-gray-500">Trạng thái</span>
                                <div className="mt-1"><StatusBadge status={runDetail.status} /></div>
                            </div>
                            <div>
                                <span className="text-gray-500">Thời lượng</span>
                                <div className="mt-1 font-mono">{runDetail.duration_ms ? `${(runDetail.duration_ms / 1000).toFixed(1)}s` : '-'}</div>
                            </div>
                            <div>
                                <span className="text-gray-500">Bắt đầu</span>
                                <div className="mt-1 text-xs font-mono">{runDetail.started_at ? new Date(runDetail.started_at).toLocaleString() : '-'}</div>
                            </div>
                            <div>
                                <span className="text-gray-500">Kết thúc</span>
                                <div className="mt-1 text-xs font-mono">{runDetail.finished_at ? new Date(runDetail.finished_at).toLocaleString() : '-'}</div>
                            </div>
                        </div>

                        {runDetail.error_message && (
                            <div>
                                <span className="text-sm font-medium text-red-600">Lỗi</span>
                                <pre className="mt-1 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-800 whitespace-pre-wrap break-words max-h-32 overflow-y-auto">
                                    {runDetail.error_message}
                                </pre>
                            </div>
                        )}

                        {Array.isArray(runDetail.notification_results) && runDetail.notification_results.length > 0 && (
                            <div>
                                <span className="text-sm font-medium text-gray-700">Kết quả đầu ra</span>
                                <div className="mt-1 space-y-2">
                                    {runDetail.notification_results.map((r, i) => (
                                        <div
                                            key={i}
                                            className={`p-2 border rounded-lg text-xs ${r.ok ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}
                                        >
                                            <div className="flex items-center gap-2">
                                                <Badge variant={r.ok ? 'success' : 'danger'}>{r.type}</Badge>
                                                <span className={r.ok ? 'text-green-700' : 'text-red-700'}>
                                                    {r.ok ? 'đã gửi thành công' : 'thất bại'}
                                                </span>
                                            </div>
                                            {r.error && (
                                                <pre className="mt-1 text-red-800 whitespace-pre-wrap break-words">{r.error}</pre>
                                            )}
                                        </div>
                                    ))}
                                </div>
                                <details className="mt-2">
                                    <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700 select-none">
                                        Xem JSON gốc
                                    </summary>
                                    <pre className="mt-1 p-3 bg-gray-50 border border-gray-200 rounded-lg text-xs whitespace-pre-wrap break-words max-h-40 overflow-y-auto">
                                        {JSON.stringify(runDetail.notification_results, null, 2)}
                                    </pre>
                                </details>
                            </div>
                        )}

                        <RunOutputView output={runDetail.output_summary} />

                        <div className="pt-3 border-t border-gray-100 text-xs text-gray-500">
                            Để xem transcript đầy đủ và chat về kết quả, vào{' '}
                            <strong>Instrument Intelligence</strong> → mở insight tương ứng.
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
}

function parseOutputContent(raw) {
    if (!raw) return { text: '', thinking: '' };
    try {
        const cleaned = raw
            .replace(/^'|'$/g, '')
            .replace(/'/g, '"')
            .replace(/True/g, 'true')
            .replace(/False/g, 'false')
            .replace(/None/g, 'null');
        const blocks = JSON.parse(cleaned);
        if (Array.isArray(blocks)) {
            let text = '';
            let thinking = '';
            for (const block of blocks) {
                if (block.type === 'text' && block.text) text += block.text + '\n';
                if (block.type === 'thinking' && block.thinking) thinking += block.thinking + '\n';
            }
            return { text: text.trim(), thinking: thinking.trim() };
        }
    } catch { /* fallback */ }
    return { text: raw, thinking: '' };
}

function RunOutputView({ output }) {
    const [viewMode, setViewMode] = useState('markdown');
    const { text, thinking } = parseOutputContent(output);

    if (!output) {
        return (
            <div>
                <span className="text-sm font-medium text-gray-700">Kết quả</span>
                <p className="mt-1 text-gray-400 text-sm">Không có kết quả</p>
            </div>
        );
    }

    return (
        <div>
            <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Kết quả</span>
                <div className="flex gap-1">
                    <button
                        onClick={() => setViewMode('markdown')}
                        className={`px-2 py-0.5 text-xs rounded ${viewMode === 'markdown' ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100'}`}
                    >
                        Markdown
                    </button>
                    <button
                        onClick={() => setViewMode('raw')}
                        className={`px-2 py-0.5 text-xs rounded ${viewMode === 'raw' ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100'}`}
                    >
                        Thô
                    </button>
                </div>
            </div>

            {viewMode === 'markdown' ? (
                <div className="space-y-3">
                    <div className="p-4 bg-white border border-gray-200 rounded-lg max-h-96 overflow-y-auto">
                        <div className="prose prose-sm max-w-none text-gray-700">
                            <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                                {text}
                            </ReactMarkdown>
                        </div>
                    </div>
                    {thinking && (
                        <details className="border border-purple-200 rounded-lg">
                            <summary className="px-4 py-2 text-xs font-medium text-purple-600 cursor-pointer hover:bg-purple-50 select-none">
                                Suy luận
                            </summary>
                            <div className="px-4 py-3 border-t border-purple-100 max-h-64 overflow-y-auto">
                                <div className="prose prose-sm max-w-none text-purple-700">
                                    <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                                        {thinking}
                                    </ReactMarkdown>
                                </div>
                            </div>
                        </details>
                    )}
                </div>
            ) : (
                <pre className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-xs whitespace-pre-wrap break-words max-h-96 overflow-y-auto">
                    {output}
                </pre>
            )}
        </div>
    );
}
